import type { ContainerMetadata } from "../types";

const LOSSLESS_EXTENSIONS = new Set(["flac", "wav", "wave", "aiff", "aif", "alac"]);

export function isLosslessExtension(extension: string): boolean {
  return LOSSLESS_EXTENSIONS.has(extension.toLowerCase());
}

export function parseContainerMetadata(
  buffer: ArrayBuffer,
  fileName: string,
): ContainerMetadata {
  const view = new DataView(buffer);
  const extension = extensionOf(fileName);
  const metadata: ContainerMetadata = {
    container: extension.toUpperCase(),
    declaredLossless: isLosslessExtension(extension),
  };

  try {
    if (readAscii(view, 0, 4) === "RIFF" && readAscii(view, 8, 4) === "WAVE") {
      metadata.container = "WAV";
      let offset = 12;
      while (offset + 8 <= view.byteLength) {
        const id = readAscii(view, offset, 4);
        const size = view.getUint32(offset + 4, true);
        if (id === "fmt " && size >= 16) {
          metadata.channels = view.getUint16(offset + 10, true);
          metadata.sampleRate = view.getUint32(offset + 12, true);
          metadata.bitDepth = view.getUint16(offset + 22, true);
          break;
        }
        offset += 8 + size + (size % 2);
      }
    } else if (readAscii(view, 0, 4) === "fLaC" && view.byteLength > 26) {
      metadata.container = "FLAC";
      const blockType = view.getUint8(4) & 0x7f;
      if (blockType === 0) {
        const a = view.getUint8(18);
        const b = view.getUint8(19);
        const c = view.getUint8(20);
        const d = view.getUint8(21);
        metadata.sampleRate = (a << 12) | (b << 4) | (c >> 4);
        metadata.channels = ((c >> 1) & 0x07) + 1;
        metadata.bitDepth = (((c & 0x01) << 4) | (d >> 4)) + 1;
      }
    }
  } catch {
    // Metadata parsing is best-effort; decoded PCM remains authoritative.
  }

  return metadata;
}

export function extensionOf(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() ?? "";
}

function readAscii(view: DataView, offset: number, length: number): string {
  return Array.from(
    { length },
    (_, index) => String.fromCharCode(view.getUint8(offset + index)),
  ).join("");
}
